// DesktopQuotationBuilder.jsx
// Desktop (wide screen) layout for the quotation builder. Renders the same
// state/handlers exposed by useQuotationBuilder as an admin form + items table.
import { B, fonts, Btn, Input, Card } from "../../../../components/UI";
import GSTSegmentHint from "../../../../components/GSTSegmentHint";
import TransportSelect from "../../../../components/TransportSelect";
import { withCloudinaryAutoFormatQuality, withCloudinaryProductCardOptimization } from "../../../../utils/cloudinary";
import { getGSTValidationMessage } from "../../../../utils/validation";
import { getCustomerLabel, getDesignLabel, getDesignImage, formatCurrency, toTitleCase, INDIAN_STATES, formatLastSavedAt } from "./useQuotationBuilder";

export default function DesktopQuotationBuilder({ qb }) {
  const {
    customers,
    transports,
    designs,
    quotations,
    initialQuotation,
    readOnly,
    saving,
    onSave,
    onBack,
    onCancelEdit,
    customerLookup,
    setCustomerLookup,
    customerForm,
    setCustomerForm,
    setCustomerField,
    applyCustomer,
    availableCustomers,
    handleCustomerQueryChange,
    selectedCustomer,
    quotationNumber,
    setQuotationNumber,
    billNumber,
    setBillNumber,
    isInvoice,
    createdAt,
    setCreatedAt,
    nextQuotationNumber,
    rows,
    updateRow,
    addRow,
    addRowAfter,
    removeRow,
    designMap,
    handleDesignQueryChange,
    selectDesignForRow,
    commitManualDesign,
    handleAddDesignMobile,
    handleDesignRowEnter,
    designInputRefs,
    quantityInputRefs,
    nextManualDesignNumber,
    nextMobileDesignRow,
    manualDesignValue,
    setManualDesignValue,
    showDesignSearch,
    setShowDesignSearch,
    designSearchTerm,
    setDesignSearchTerm,
    designSearchResults,
    previewImageUrl,
    setPreviewImageUrl,
    discountPercentageValue,
    setDiscountPercentageValue,
    packagingChargeValue,
    setPackagingChargeValue,
    transportChargeValue,
    setTransportChargeValue,
    allowPriceEdit,
    allowDiscount,
    minOrderValue,
    autoDiscountEligible,
    showFirmName,
    showPackagingCharges,
    computedItems,
    subtotalAmount,
    totalQuantity,
    discountPercentage,
    shouldApplyDiscount,
    discountAmount,
    packagingCharges,
    transportCharges,
    grossAmount,
    cgstAmount,
    sgstAmount,
    igstAmount,
    totalTaxAmount,
    grandTotal,
    roundOffAmount,
    gstError,
    setGstError,
    gstFocused,
    setGstFocused,
    moreOptionsOpen,
    setMoreOptionsOpen,
    showCustomerSheet,
    setShowCustomerSheet,
    sendWhatsApp,
    setSendWhatsApp,
    isDraftRestored,
    lastSavedAt,
    clearDraft,
    showDraftBanner,
    showAutoSaveIndicator,
    formattedLastSavedAt,
    mobileAutoSaveText,
    filledItemCount,
    handleSaveQuotation,
    handleClearQuotation,
    handleGenerateBillNumber,
  } = qb;

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <p style={{ margin: 0, fontSize: 12, color: B.textLight, fontFamily: fonts.body }}>Search customer, auto-fill details, or create a new customer while making quotation</p>
        {readOnly ? "View Quotation" : initialQuotation ? "Edit Quotation" : "Create Quotation"}
      </div>

      {showDraftBanner && (
        <div
          className="qb-draft-banner"
        >
          <span style={{ fontWeight: 700 }}>✓</span>
          <span>
            Draft restored from {formatLastSavedAt(lastSavedAt) || "previous session"}.
            Continue editing or{" "}
            <button
              type="button"
              onClick={handleClearQuotation}
              style={{
                background: "none",
                border: "none",
                color: "#1e7e34",
                textDecoration: "underline",
                cursor: "pointer",
                fontSize: 13,
                fontFamily: fonts.body,
                padding: 0,
              }}
            >
              clear the draft
            </button>
            .
          </span>
        </div>
      )}

      {showAutoSaveIndicator && (
        <div
          style={{
            marginTop: 6,
            marginBottom: 4,
            fontSize: 11,
            color: B.textLight,
            fontFamily: fonts.body,
            display: "flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          <span style={{ fontSize: 10, color: "#4caf50" }}>●</span>
          <span>Auto-saved at {formattedLastSavedAt}</span>
        </div>
      )}

      <Card style={{ marginBottom: 18 }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(220px, 360px))", gap: 14, marginBottom: 12 }}>
          <Input
            label="Quotation Number"
            value={quotationNumber}
            onChange={e => setQuotationNumber(e.target.value)}
            placeholder="Enter quotation number"
            disabled={readOnly}
            required
          />
          <div style={{ marginBottom: 14 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 5 }}>
              <label className="qb-field-label">
                Bill Number (Optional)
              </label>
              {!readOnly && (
                <button
                  type="button"
                  onClick={handleGenerateBillNumber}
                  style={{
                    background: "none",
                    border: "none",
                    color: B.orange,
                    textDecoration: "underline",
                    cursor: "pointer",
                    fontSize: 11,
                    fontFamily: fonts.body,
                    padding: 0,
                  }}
                >
                  Generate
                </button>
              )}
            </div>
            <input
              value={billNumber}
              onChange={e => setBillNumber(e.target.value)}
              placeholder="Enter bill number"
              disabled={readOnly}
              className="qb-input--desktop"
            />
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <label style={{ fontSize: 12, color: B.textMid, fontWeight: 600, fontFamily: fonts.body }}>Customer</label>
            <input
              list="customer-options"
              value={customerLookup}
              onChange={e => handleCustomerQueryChange(e.target.value)}
              placeholder={showFirmName ? "Search customer name / firm / mobile" : "Search customer name / mobile"}
              disabled={readOnly}
              className="qb-input--desktop"
            />
            <datalist id="customer-options">
              {availableCustomers.map(customer => (
                <option key={customer.id} value={getCustomerLabel(customer, showFirmName)} />
              ))}
            </datalist>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <label style={{ fontSize: 12, color: B.textMid, fontWeight: 600, fontFamily: fonts.body }}>Date</label>
            <input
              type="date"
              value={createdAt}
              onChange={e => setCreatedAt(e.target.value)}
              disabled={readOnly}
              className="qb-input--desktop"
            />
          </div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(220px, 1fr))", gap: 12, marginBottom: 12 }}>
          <Input label="Customer Name" value={customerForm.customer_name} onChange={e => setCustomerField("customer_name", toTitleCase(e.target.value))} disabled={readOnly} />
          <div onFocus={() => setGstFocused(true)} onBlur={() => setGstFocused(false)}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 5 }}>
              <label className="qb-field-label">GST Number</label>
              {!readOnly && (
                <button
                  type="button"
                  onClick={() => { setCustomerField("gst_number", "URP"); setGstError(getGSTValidationMessage("URP")); }}
                  className="qb-link"
                >
                  URP
                </button>
              )}
            </div>
            <input
              value={customerForm.gst_number || ""}
              onChange={e => { setCustomerField("gst_number", e.target.value.toUpperCase()); setGstError(getGSTValidationMessage(e.target.value)); }}
              disabled={readOnly}
              style={{ background: B.white, border: `1.5px solid ${B.border}`, borderRadius: 8, padding: "10px 13px", color: B.text, fontSize: 13, outline: "none", boxSizing: "border-box", fontFamily: fonts.body, width: "100%" }}
            />
            {!readOnly && gstFocused && <GSTSegmentHint value={customerForm.gst_number} />}
            {gstError && !readOnly && (
              <div style={{ fontSize: 11, color: B.red, marginTop: 4, fontFamily: fonts.body }}>{gstError}</div>
            )}
          </div>
          <Input label="Address" value={customerForm.address} onChange={e => setCustomerField("address", toTitleCase(e.target.value))} disabled={readOnly} />
          <Input label="City" value={customerForm.city} onChange={e => setCustomerField("city", toTitleCase(e.target.value))} disabled={readOnly} />
          <Input label="Pincode" value={customerForm.pincode || ""} onChange={e => setCustomerField("pincode", e.target.value.replace(/\D/g, "").slice(0, 6))} disabled={readOnly} />
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <label style={{ fontSize: 12, color: B.textMid, fontWeight: 600, fontFamily: fonts.body }}>State</label>
            <select
              value={customerForm.state || ""}
              onChange={e => setCustomerField("state", e.target.value)}
              disabled={readOnly}
              className="qb-input--desktop"
            >
              <option value="">Select state</option>
              {INDIAN_STATES.map(state => (
                <option key={state} value={state}>{state}</option>
              ))}
            </select>
          </div>
          <Input label="Mobile Number" value={customerForm.mobile_number} onChange={e => setCustomerField("mobile_number", e.target.value)} disabled={readOnly} />
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <TransportSelect
              label="Transport"
              labelStyle={{ fontSize: 12, color: B.textMid, fontWeight: 600, fontFamily: fonts.body }}
              transports={transports}
              value={{ id: customerForm.transport_id || "", name: customerForm.transport_name || "", gst: customerForm.transport_gst || "" }}
              onChange={({ id, name, gst }) => { setCustomerField("transport_id", id); setCustomerField("transport_name", name || ""); setCustomerField("transport_gst", gst || ""); }}
              disabled={readOnly}
            />
          </div>
        </div>

        <div style={{ overflowX: "auto", border: `1px solid ${B.borderLight}`, borderRadius: 12 }}>
          <table className="qb-table">
            <thead>
              <tr style={{ background: B.orangeFaint }}>
                {/* "Sr." hidden — may be restored in future */}
                {["Design Number", "Design Image", "Quantity", "Rate", "Sub-total", "Action"].map(header => (
                  <th key={header} className="qb-th">
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, index) => {
                const computedItem = computedItems[index];
                return (
                  <tr key={row.uid}>
                    {/* Serial number column hidden — may be restored in future */}
                    {/* <td style={{ padding: "10px", borderBottom: `1px solid ${B.borderLight}`, fontFamily: fonts.body }}>{index + 1}</td> */}
                    <td className="qb-td">
                      D. No: <input
                        ref={el => {
                          if (el) designInputRefs.current.set(row.uid, el);
                          else designInputRefs.current.delete(row.uid);
                        }}
                        list={`design-options-${row.uid}`}
                        value={row.designQuery || ""}
                        onChange={e => handleDesignQueryChange(row.uid, e.target.value)}
                        onKeyDown={e => handleDesignRowEnter(e, row.uid)}
                        disabled={readOnly}
                        placeholder="Search design number"
                        style={{ width: "80%", background: B.white, border: `1.5px solid ${B.border}`, borderRadius: 8, padding: "8px 10px", fontSize: 12, color: B.text, fontFamily: fonts.body, outline: "none" }}
                      />
                      <datalist id={`design-options-${row.uid}`}>
                        {designs.map(design => (
                          <option key={design.id} value={getDesignLabel(design)} />
                        ))}
                      </datalist>
                    </td>
                    <td className="qb-td">
                      {computedItem.imageUrl ? (
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <img
                            src={withCloudinaryProductCardOptimization(computedItem.imageUrl)}
                            alt={`Design ${computedItem.designNo}`}
                            style={{ width: 64, height: 82, objectFit: "cover", borderRadius: 8, border: `1px solid ${B.borderLight}`, display: "block" }}
                          />
                          <button
                            type="button"
                            onClick={() => setPreviewImageUrl(computedItem.imageUrl)}
                            className="qb-view-btn"
                            title="Open larger preview"
                          >
                            View
                          </button>
                        </div>
                      ) : null}
                    </td>
                    <td className="qb-td">
                      <input
                        ref={el => {
                          if (el) quantityInputRefs.current.set(row.uid, el);
                          else quantityInputRefs.current.delete(row.uid);
                        }}
                        type="number"
                        min={1}
                        value={row.quantity}
                        onChange={e => updateRow(row.uid, "quantity", e.target.value)}
                        onKeyDown={e => handleDesignRowEnter(e, row.uid)}
                        disabled={readOnly}
                        style={{ width: 90, background: B.white, border: `1.5px solid ${B.border}`, borderRadius: 8, padding: "8px 10px", fontSize: 12, color: B.text, fontFamily: fonts.body, outline: "none" }}
                      />
                    </td>
                    <td style={{ padding: "10px", borderBottom: `1px solid ${B.borderLight}`, fontFamily: fonts.body }}>
                      {allowPriceEdit ? (
                        <input
                          type="number"
                          min={0}
                          step="1"
                          value={row.unitPrice}
                          onChange={e => updateRow(row.uid, "unitPrice", e.target.value)}
                          onKeyDown={e => handleDesignRowEnter(e, row.uid)}
                          disabled={readOnly || !allowPriceEdit}
                          style={{ width: 110, background: B.white, border: `1.5px solid ${B.border}`, borderRadius: 8, padding: "8px 10px", fontSize: 12, color: B.text, fontFamily: fonts.body, outline: "none" }}
                        />
                      ) : (
                        formatCurrency(computedItem.price)
                      )}
                    </td>
                    <td style={{ padding: "10px", borderBottom: `1px solid ${B.borderLight}`, fontFamily: fonts.body, fontWeight: 700 }}>
                      {formatCurrency(computedItem.subtotal)}
                    </td>
                    <td className="qb-td">
                      <Btn onClick={() => removeRow(row.uid)} variant="danger" small disabled={readOnly}>Remove</Btn>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div style={{ display: "flex", alignItems: "flex-start", gap: 24, marginTop: 14, flexWrap: "wrap" }}>
          <div style={{ flex: "1 1 0", minWidth: 460, display: "flex", flexDirection: "column", gap: 10 }}>
            <Btn onClick={addRow} variant="ghost" disabled={readOnly} style={{ alignSelf: "flex-start" }}>+ Add Item</Btn>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: "12px 20px" }}>
              {((allowDiscount || discountPercentageValue > 0)) && (
                <div>
                  <label style={{ display: "block", fontSize: 14, color: B.textMid, fontFamily: fonts.body, marginBottom: 4 }}>Discount (%)</label>
                  <input
                    type="number"
                    min={0}
                    step="any"
                    value={discountPercentageValue}
                    onChange={e => setDiscountPercentageValue(e.target.value)}
                    disabled={readOnly || !allowDiscount}
                    style={{ width: "100%", background: B.white, border: `1.5px solid ${B.border}`, borderRadius: 8, padding: "8px 10px", fontSize: 12, color: B.text, fontFamily: fonts.body, outline: "none" }}
                  />
                </div>
              )}
              {!allowDiscount && discountPercentageValue > 0 && (
                <div style={{ gridColumn: "1 / -1", fontSize: 14, color: B.textLight, fontFamily: fonts.body }}>
                  Discount setting is off; showing saved discount ({discountPercentage}%) from this {isInvoice ? "invoice" : "quotation"}.
                </div>
              )}
              {minOrderValue > 0 && (
                <div style={{ gridColumn: "1 / -1", fontSize: 13, color: autoDiscountEligible ? B.green : B.orange, fontFamily: fonts.body }}>
                  {!autoDiscountEligible
                    ? `Cannot apply auto discount because of minimum order value.`
                    : discountPercentage > 0
                      ? `Auto discount applied.`
                      : ``}
                </div>
              )}
              {showPackagingCharges && (
              <div>
                <label style={{ display: "block", fontSize: 14, color: B.textMid, fontFamily: fonts.body, marginBottom: 4 }}>Packaging Charges</label>
                <input
                  type="number"
                  min={0}
                  step="1"
                  value={packagingChargeValue}
                  onChange={e => setPackagingChargeValue(e.target.value)}
                  disabled={readOnly}
                  style={{ width: "100%", background: B.white, border: `1.5px solid ${B.border}`, borderRadius: 8, padding: "8px 10px", fontSize: 12, color: B.text, fontFamily: fonts.body, outline: "none" }}
                />
              </div>
              )}
              <div>
                <label style={{ display: "block", fontSize: 14, color: B.textMid, fontFamily: fonts.body, marginBottom: 4 }}>Transportation Charges</label>
                <input
                  type="number"
                  min={0}
                  step="1"
                  value={transportChargeValue}
                  onChange={e => setTransportChargeValue(e.target.value)}
                  disabled={readOnly}
                  style={{ width: "100%", background: B.white, border: `1.5px solid ${B.border}`, borderRadius: 8, padding: "8px 10px", fontSize: 12, color: B.text, fontFamily: fonts.body, outline: "none" }}
                />
              </div>
              <div>
                <label style={{ display: "block", fontSize: 14, color: B.textMid, fontFamily: fonts.body, marginBottom: 4 }}>Total Quantity</label>
                <div style={{ fontWeight: 700, fontSize: 13, color: B.text, fontFamily: fonts.body, padding: "8px 0" }}>{totalQuantity}</div>
              </div>
            </div>
          </div>

          <div style={{ flex: "0 0 auto", width: 340, maxWidth: "100%" }}>
            <div className="qb-total-box" style={{ width: "100%" }}>
              <div className="qb-total-row">
                <span>Sub-total</span>
                <span>{formatCurrency(subtotalAmount)}</span>
              </div>
              {allowDiscount && discountPercentageValue > 0 && (
                <div className="qb-total-row">
                  <span>Discount ({discountPercentageValue}%)</span>
                  <span>-{formatCurrency(discountAmount)}</span>
                </div>
              )}
              {totalTaxAmount > 0 && (
                <div className="qb-total-row">
                  <span>
                    Tax
                    {cgstAmount > 0 ? " (CGST 2.5% + SGST 2.5%)" : ""}
                    {igstAmount > 0 ? " (IGST 5%)" : ""}
                  </span>
                  <span>{formatCurrency(totalTaxAmount)}</span>
                </div>
              )}
              {showPackagingCharges && packagingCharges > 0 && (
                <div className="qb-total-row">
                  <span>Packaging Charges</span>
                  <span>{formatCurrency(packagingCharges)}</span>
                </div>
              )}
              {transportCharges > 0 && (
                <div className="qb-total-row">
                  <span>Transportation Charges</span>
                  <span>{formatCurrency(transportCharges)}</span>
                </div>
              )}
              {roundOffAmount !== 0 && (
                <div className="qb-total-row">
                  <span>Round Off</span>
                  <span>{roundOffAmount > 0 ? "+" : ""}{formatCurrency(roundOffAmount)}</span>
                </div>
              )}
              <div style={{ borderTop: `1px solid ${B.borderLight}`, marginTop: 6, paddingTop: 6, display: "flex", justifyContent: "space-between", fontSize: 15, fontWeight: 800, color: B.text, fontFamily: fonts.adminDisplay }}>
                <span>Grand Total</span>
                <span style={{ color: B.orange }}>{formatCurrency(grandTotal)}</span>
              </div>
            </div>
          </div>
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", marginTop: 18, gap: 14, flexWrap: "wrap" }}>
          {!readOnly && (
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                cursor: "pointer",
                fontSize: 13,
                color: B.textMid,
                fontFamily: fonts.body,
                userSelect: "none",
              }}
            >
              <input
                type="checkbox"
                checked={sendWhatsApp}
                onChange={(e) => setSendWhatsApp(e.target.checked)}
                disabled={saving}
                style={{ width: 16, height: 16, cursor: "pointer", accentColor: "#25D366" }}
              />
              <span style={{ display: "flex", alignItems: "center", gap: 5 }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="#25D366">
                  <path d="M12 2a10 10 0 00-8.7 15l-1.3 5 5.1-1.3A10 10 0 1012 2zm0 18a8 8 0 01-4.1-1.1l-.3-.2-3 .8.8-2.9-.2-.3A8 8 0 1112 20zm4.3-5.7c-.2-.1-1.3-.6-1.5-.7s-.4-.1-.6.1-.7.7-.8.8-.3.1-.5 0a6.6 6.6 0 01-2-1.2 7.3 7.3 0 01-1.4-1.8c-.1-.2 0-.3.1-.4l.3-.3c.1-.1.2-.2.3-.4.1-.2 0-.3 0-.4s-.6-1.4-.8-1.9c-.2-.5-.4-.4-.6-.4h-.5c-.2 0-.4.1-.6.3s-.8.8-.8 2 .8 2.4.9 2.6a9.8 9.8 0 003.8 3.6c.5.2.9.4 1.2.5.5.2 1 .2 1.4.1.4-.1 1.3-.5 1.5-1 .2-.5.2-.9.1-1-.1-.1-.3-.2-.5-.3z"/>
                </svg>
                Send on WhatsApp
              </span>
            </label>
          )}
          <div style={{ display: "flex", gap: 10 }}>
            {initialQuotation && (
              <Btn onClick={onCancelEdit} variant="ghost">
                {readOnly ? "Close" : "Cancel Edit"}
              </Btn>
            )}
            {!readOnly && !initialQuotation && (
              <Btn onClick={handleClearQuotation} variant="danger" disabled={saving}>
                Clear Quotation
              </Btn>
            )}
            {!readOnly && (
              <Btn onClick={handleSaveQuotation} variant="primary" disabled={saving}>
                {saving ? "Saving..." : initialQuotation ? "Update Quotation" : "Save Quotation"}
              </Btn>
            )}
          </div>
        </div>
      </Card>

      {previewImageUrl && (
        <div
          role="dialog"
          aria-modal="true"
          onClick={() => setPreviewImageUrl("")}
          className="qb-preview-overlay"
        >
          <div
            onClick={e => e.stopPropagation()}
            className="qb-preview-box"
          >
            <button
              type="button"
              onClick={() => setPreviewImageUrl("")}
              className="qb-preview-close"
              aria-label="Close preview"
            >
              ✕
            </button>
            <img src={withCloudinaryAutoFormatQuality(previewImageUrl)} alt="Design preview" className="qb-preview-img" />
          </div>
        </div>
      )}
    </div>
  );
}
